import type { LineLog, RecordKind } from "../types.js";
import type { Amount, Count, Effort, Hurdles, Item, Load, Rest, RestValue, Row, RunParts } from "./types.js";

/** Why a definition's rows were refused, in words for the person who sent them. */
/** The schema of `Row`; raised when a change would make stored rows read differently or fail `validRows`. */
export const ROWS_VERSION = 1;

export class RowsError extends Error {}

const MAX_ROWS = 200;
const MAX_ITEMS = 40;
const MAX_TEXT = 500;
const MAX_ID = 64;
const MAX_LIST = 20;

/** The rows rebuilt from what was sent, every field checked; anything it does not know is refused (DESIGN.md §25.4). */
export function validRows(value: unknown): Row[] {
  const rows = list(value, "rows", MAX_ROWS);
  const valid = rows.map((raw): Row => {
    const r = record(raw, "a row");
    const id = text(r.id, "a row's id", MAX_ID, false);
    switch (r.type) {
      case "item":
        keys(r, ["type", "id", "item"]);
        return { type: "item", id, item: validItem(r.item) };
      case "rounds": {
        keys(r, ["type", "id", "sets", "effort", "note", "style", "items"]);
        if (r.style !== "list" && r.style !== "inline") throw new RowsError("A round is a list or inline.");
        const row: Row = { type: "rounds", id, sets: validCount(r.sets, "a round's sets"), style: r.style, items: list(r.items, "a round's items", MAX_ITEMS).map(validItem) };
        if (r.effort !== undefined) row.effort = validEffort(r.effort);
        if (r.note !== undefined) row.note = text(r.note, "a round's note", MAX_TEXT);
        return row;
      }
      case "section":
        keys(r, ["type", "id", "name"]);
        if (r.name !== "Prep" && r.name !== "Main") throw new RowsError("A section is Prep or Main.");
        return { type: "section", id, name: r.name };
      case "rest":
        keys(r, ["type", "id", "seconds"]);
        return { type: "rest", id, seconds: validCount(r.seconds, "a rest") };
      case "instruction": {
        keys(r, ["type", "id", "text", "under"]);
        const row: Row = { type: "instruction", id, text: text(r.text, "an instruction", MAX_TEXT) };
        if (r.under !== undefined) row.under = yes(r.under, "under");
        return row;
      }
      case "reference":
        keys(r, ["type", "id", "label"]);
        return { type: "reference", id, label: text(r.label, "a session's label", MAX_TEXT, false) };
      case "written":
        keys(r, ["type", "id", "text"]);
        return { type: "written", id, text: text(r.text, "a line", MAX_TEXT) };
      default:
        throw new RowsError("A row has an unknown type.");
    }
  });
  // Ids are how the form finds a row to edit: two alike and an edit lands on the wrong one.
  const ids = valid.flatMap((row) => [row.id, ...(row.type === "item" ? [row.item.id] : row.type === "rounds" ? row.items.map((i) => i.id) : [])]);
  if (new Set(ids).size !== ids.length) throw new RowsError("Two rows have the same id.");
  return valid;
}

const MAX_LOGS = 60;
const MAX_LOG_LINE = 1000;
const MAX_MEMBER = 40;
const RECORD_KINDS: readonly RecordKind[] = ["weightReps", "reps", "time", "done"];

/** A definition's `logs` (DESIGN.md §32.6) rebuilt from what was sent, every field checked. */
export function validLogs(value: unknown): LineLog[] {
  const seen = new Set<string>();
  return list(value, "logs", MAX_LOGS).map((raw): LineLog => {
    const v = record(raw, "a line's log");
    keys(v, ["line", "text", "record", "member"]);
    const log: LineLog = { line: whole(v.line, "a log's line", 100_000), text: text(v.text, "a logged line", MAX_LOG_LINE), record: recordKind(v.record) };
    if (v.member !== undefined) log.member = whole(v.member, "a log's member", MAX_MEMBER);
    const key = `${log.line} ${log.member ?? ""}`;
    if (seen.has(key)) throw new RowsError("Two logs are for the same line.");
    seen.add(key);
    return log;
  });
}

function recordKind(value: unknown): RecordKind {
  if (typeof value !== "string" || !RECORD_KINDS.includes(value as RecordKind)) throw new RowsError("A line logs as weight × reps, reps, time or done.");
  return value as RecordKind;
}

function whole(value: unknown, label: string, max: number): number {
  if (typeof value !== "number" || !Number.isInteger(value) || value < 0 || value > max) throw new RowsError(`Send ${label} as a whole number.`);
  return value;
}

function validItem(value: unknown): Item {
  const v = record(value, "an item");
  keys(v, ["id", "kind", "rounds", "sets", "amount", "name", "run", "perSide", "effort", "load", "hurdles", "rest", "target", "cues", "dashNotes", "onlyFor", "subNotes", "record"]);
  const kinds = ["lift", "run", "hurdles", "drill", "hold", "cardio"];
  if (typeof v.kind !== "string" || !kinds.includes(v.kind)) throw new RowsError("An item's kind is not one the form knows.");
  const item: Item = { id: text(v.id, "an item's id", MAX_ID, false), kind: v.kind as Item["kind"], name: text(v.name, "an item's name", MAX_TEXT) };
  if (v.rounds !== undefined) item.rounds = validCount(v.rounds, "rounds");
  if (v.sets !== undefined) item.sets = validCount(v.sets, "sets");
  if (v.amount !== undefined) item.amount = validAmount(v.amount);
  if (v.run !== undefined) item.run = validRun(v.run);
  if (v.perSide !== undefined) item.perSide = yes(v.perSide, "each side");
  if (v.effort !== undefined) item.effort = validEffort(v.effort);
  if (v.load !== undefined) item.load = validLoad(v.load);
  if (v.hurdles !== undefined) item.hurdles = validHurdles(v.hurdles);
  if (v.rest !== undefined) item.rest = validRest(v.rest);
  if (v.target !== undefined) item.target = text(v.target, "a target", MAX_TEXT);
  if (v.cues !== undefined) item.cues = texts(v.cues, "cues");
  if (v.dashNotes !== undefined) item.dashNotes = texts(v.dashNotes, "notes");
  if (v.onlyFor !== undefined) item.onlyFor = text(v.onlyFor, "a group", 60);
  if (v.subNotes !== undefined) item.subNotes = texts(v.subNotes, "notes under an item");
  if (v.record !== undefined) {
    if (typeof v.record !== "string" || !["weightReps", "reps", "time", "done"].includes(v.record)) throw new RowsError("An item logs as weight × reps, reps, time or done.");
    item.record = v.record as RecordKind;
  }
  return item;
}

function validCount(value: unknown, label: string): Count {
  const v = record(value, label);
  keys(v, ["n", "upper"]);
  const count: Count = { n: number(v.n, label) };
  if (count.n <= 0) throw new RowsError(`${capital(label)} is more than nothing.`);
  if (v.upper !== undefined) {
    count.upper = number(v.upper, label);
    if (count.upper <= count.n) throw new RowsError(`A range of ${label} runs from fewer to more.`);
  }
  return count;
}

function capital(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

function validAmount(value: unknown): Amount {
  const v = record(value, "an amount");
  keys(v, ["n", "upper", "unit", "parts", "between", "bracketed"]);
  const units = ["reps", "s", "min", "m", "H", "laps"];
  if (typeof v.unit !== "string" || !units.includes(v.unit)) throw new RowsError("An amount's unit is not one the form knows.");
  const amount: Amount = { ...validCount({ n: v.n, ...(v.upper !== undefined ? { upper: v.upper } : {}) }, "an amount"), unit: v.unit as Amount["unit"] };
  if (v.parts !== undefined) amount.parts = list(v.parts, "parts", MAX_LIST).map((p) => number(p, "a part"));
  if (v.between !== undefined) amount.between = text(v.between, "what is between parts", 40);
  if (v.bracketed !== undefined) amount.bracketed = yes(v.bracketed, "bracketed");
  return amount;
}

function validRun(value: unknown): RunParts {
  const v = record(value, "a run");
  keys(v, ["kind", "start", "uphill", "surface"]);
  const kinds = ["sprint", "accel", "tempo", "resisted", "skips", "slowBuild", "timeTrial", "run"];
  if (typeof v.kind !== "string" || !kinds.includes(v.kind)) throw new RowsError("A run's kind is not one the form knows.");
  const run: RunParts = { kind: v.kind as RunParts["kind"] };
  if (v.start !== undefined) {
    if (typeof v.start !== "string" || !["blocks", "3-point", "walk-in", "standing", "fly"].includes(v.start)) throw new RowsError("A run's start is not one the form knows.");
    run.start = v.start as RunParts["start"];
  }
  if (v.uphill !== undefined) {
    if (typeof v.uphill !== "boolean") throw new RowsError("Send uphill as true or false.");
    run.uphill = v.uphill;
  }
  if (v.surface !== undefined) {
    if (typeof v.surface !== "string" || !["grass", "turf", "track", "sand"].includes(v.surface)) throw new RowsError("A run's surface is not one the form knows.");
    run.surface = v.surface as RunParts["surface"];
  }
  return run;
}

function validEffort(value: unknown): Effort {
  const v = record(value, "an effort");
  if (v.word !== undefined) {
    keys(v, ["word"]);
    if (typeof v.word !== "string" || !["very light", "light", "medium", "heavy", "very heavy"].includes(v.word)) throw new RowsError("An effort is one of his five words.");
    return { word: v.word as Extract<Effort, { word: unknown }>["word"] };
  }
  keys(v, ["percent", "how", "of"]);
  if (v.how !== "at" && v.how !== "to") throw new RowsError("A percent is at or to.");
  const effort: Effort = { percent: validCount(v.percent, "a percent"), how: v.how };
  if (v.of !== undefined) effort.of = text(v.of, "what a percent is of", 100);
  return effort;
}

function validLoad(value: unknown): Load {
  const v = record(value, "a load");
  if (v.sled !== undefined) {
    keys(v, ["sled"]);
    if (v.sled !== "light" && v.sled !== "medium" && v.sled !== "heavy") throw new RowsError("A sled is light, medium or heavy.");
    return { sled: v.sled };
  }
  keys(v, ["machine", "kg", "assisted", "decreasing"]);
  if (v.machine !== "1080") throw new RowsError("A load is a sled or the 1080.");
  const load: Load = { machine: "1080" };
  if (v.kg !== undefined) load.kg = validCount(v.kg, "a load");
  if (v.assisted !== undefined) {
    if (typeof v.assisted !== "boolean") throw new RowsError("Send assisted as true or false.");
    load.assisted = v.assisted;
  }
  if (v.decreasing !== undefined) load.decreasing = list(v.decreasing, "loads", MAX_LIST).map((p) => number(p, "a load"));
  return load;
}

function validHurdles(value: unknown): Hurdles {
  const v = record(value, "hurdles");
  keys(v, ["height", "discount", "spacing", "steps", "low", "joiner"]);
  const h: Hurdles = {};
  for (const key of ["height", "discount", "spacing", "steps"] as const) if (v[key] !== undefined) h[key] = text(v[key], `a hurdle ${key}`, 100);
  if (v.low !== undefined) {
    if (v.low !== "low" && v.low !== "very low") throw new RowsError("Hurdles are low or very low.");
    h.low = v.low;
  }
  if (v.joiner !== undefined) {
    if (v.joiner !== "and") throw new RowsError("A hurdle's height and discount are joined with and.");
    h.joiner = "and";
  }
  return h;
}

function validRest(value: unknown): Rest {
  const v = record(value, "a rest");
  keys(v, ["reps", "sets"]);
  const rest: Rest = {};
  if (v.reps !== undefined) rest.reps = validRestValue(v.reps);
  if (v.sets !== undefined) rest.sets = validRestValue(v.sets);
  return rest;
}

function validRestValue(value: unknown): RestValue {
  const v = record(value, "a rest");
  if (v.walkBack !== undefined) {
    keys(v, ["walkBack"]);
    return { walkBack: yes(v.walkBack, "walk-back") };
  }
  if (v.full !== undefined) {
    keys(v, ["full"]);
    return { full: yes(v.full, "full rest") };
  }
  keys(v, ["seconds", "as"]);
  const rest: RestValue = { seconds: validCount(v.seconds, "a rest") };
  if (v.as !== undefined) {
    if (v.as !== "s" && v.as !== "min" && v.as !== "bare") throw new RowsError("A rest is written in s, min or bare minutes.");
    rest.as = v.as;
  }
  return rest;
}

function record(value: unknown, label: string): Record<string, unknown> {
  if (typeof value !== "object" || value === null || Array.isArray(value)) throw new RowsError(`Send ${label} as an object.`);
  return value as Record<string, unknown>;
}

function keys(value: Record<string, unknown>, allowed: string[]): void {
  for (const key of Object.keys(value)) if (!allowed.includes(key)) throw new RowsError(`A row has an unknown field, ${key}.`);
}

function list(value: unknown, label: string, max: number): unknown[] {
  if (!Array.isArray(value)) throw new RowsError(`Send the ${label} as a list.`);
  if (value.length > max) throw new RowsError(`Too many ${label}.`);
  return value;
}

function text(value: unknown, label: string, max: number, allowEmpty = true): string {
  if (typeof value !== "string") throw new RowsError(`Send ${label} as text.`);
  // One line of his sheet each: a line break would split it into two lines nobody wrote.
  if (/[\u0000-\u001f\u007f]/.test(value)) throw new RowsError(`${capital(label)} is one line, without breaks.`);
  if (!allowEmpty && value.trim() === "") throw new RowsError(`${capital(label)} can't be empty.`);
  if (value.length > max) throw new RowsError(`${capital(label)} is too long.`);
  return value;
}

function texts(value: unknown, label: string): string[] {
  return list(value, label, MAX_LIST).map((t) => text(t, label, MAX_TEXT));
}

function number(value: unknown, label: string): number {
  if (typeof value !== "number" || !Number.isFinite(value) || value < 0 || value > 100_000) throw new RowsError(`Send ${label} as a number.`);
  return value;
}

function yes(value: unknown, label: string): true {
  if (value !== true) throw new RowsError(`Send ${label} as true, or leave it out.`);
  return true;
}
